import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Grid,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { CheckCircle, Close, Edit, Star, Refresh } from '@mui/icons-material';

import PageMeta from '../../components/PageMeta';
import useDebounce from '../../hooks/useDebounce';
import { approveSupportReview, featureSupportReview, getSupportReviews, rejectSupportReview, updateSupportReview, API_BASE_URL } from '../../services/api';
import { hasEndpointAccess } from '../../utils/rbac';
import CustomPagination from '../../components/common/CustomPagination';

const getMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE_URL}${cleanUrl}`;
};

const addSerialNumbers = (data, page, pageSize) =>
  data.map((row, idx) => ({
    ...row,
    sn: page * pageSize + idx + 1,
  }));

const notifyPendingReviewsCountRefresh = () => {
  window.dispatchEvent(new Event('support-reviews:pending-count-refresh'));
};

const truncate = (value, max = 80) => {
  const str = value == null ? '' : String(value);
  if (str.length <= max) return str;
  return `${str.slice(0, max)}…`;
};

export default function ReviewsAdminPage() {
  const user = useSelector((state) => state.auth.user);
  const canMediate = useMemo(() => hasEndpointAccess(user, 'admin-review-mediate', 'POST'), [user]);

  const [statusTab, setStatusTab] = useState('pending');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const totalPages = Math.ceil(rowCount / pageSize) || 1;

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setPageSize(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const handleRowsPerPageChange = (e) => {
    const val = e.target.value;
    if (val === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(String(pageSize));
    } else {
      setPageSize(val);
      setPage(0);
    }
  };

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSupportReviews({
        status: statusTab,
        search: debouncedSearch || undefined,
        page: page + 1,
        page_size: pageSize,
      });
      const results = Array.isArray(data?.results) ? data.results : [];
      setRowCount(Number(data?.count || 0));
      setRows(addSerialNumbers(results, page, pageSize));
    } catch (err) {
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, pageSize, statusTab]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleOpenEdit = useCallback((row) => {
    setEditRow(row);
    setEditDisplayName(row?.display_name || '');
    setEditLocation(row?.location || '');
    setEditOpen(true);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditOpen(false);
    setEditRow(null);
    setEditDisplayName('');
    setEditLocation('');
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editRow?.id) {
      handleCloseEdit();
      return;
    }
    setSavingEdit(true);
    try {
      const updated = await updateSupportReview(editRow.id, {
        display_name: editDisplayName || '',
        location: editLocation || '',
      });
      setRows((prev) =>
        prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r))
      );
      notifyPendingReviewsCountRefresh();
      handleCloseEdit();
    } catch (err) {
    } finally {
      setSavingEdit(false);
    }
  }, [editDisplayName, editLocation, editRow?.id, handleCloseEdit]);

  const handleApprove = useCallback(async (id) => {
    try {
      await approveSupportReview(id);
      notifyPendingReviewsCountRefresh();
      loadReviews();
    } catch (err) {
    }
  }, [loadReviews]);

  const handleReject = useCallback(async (id) => {
    try {
      await rejectSupportReview(id);
      notifyPendingReviewsCountRefresh();
      loadReviews();
    } catch (err) {
    }
  }, [loadReviews]);

  const handleToggleFeatured = useCallback(async (row) => {
    if (!row?.id) return;
    try {
      await featureSupportReview(row.id, !row.is_featured);
      loadReviews();
    } catch (err) {
    }
  }, [loadReviews]);

  const columns = useMemo(() => ([
    { field: 'sn', headerName: '#', width: 70 },
    { field: 'rating', headerName: 'Rating', width: 90 },
    {
      field: 'permission_to_publish',
      headerName: 'Publish?',
      width: 110,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'success' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => (
        <Chip size="small" label={params.value} variant="outlined" />
      ),
    },
    {
      field: 'is_featured',
      headerName: 'Featured',
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? 'Featured' : 'No'}
          color={params.value ? 'warning' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'display_name',
      headerName: 'Display name',
      minWidth: 160,
      flex: 1,
    },
    {
      field: 'location',
      headerName: 'Location',
      minWidth: 140,
      flex: 1,
      valueGetter: (params) => params?.row?.location || '',
    },
    {
      field: 'product_category',
      headerName: 'Category',
      minWidth: 140,
      flex: 1,
      valueGetter: (params) => params?.row?.product_category || '',
    },
    {
      field: 'title',
      headerName: 'Title',
      minWidth: 170,
      flex: 1,
      valueGetter: (params) => params?.row?.title || '',
    },
    {
      field: 'comment',
      headerName: 'Comment',
      minWidth: 260,
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.value || ''} placement="top">
          <span>{truncate(params.value, 120)}</span>
        </Tooltip>
      ),
    },
    {
      field: 'proof_image',
      headerName: 'Proof',
      width: 100,
      renderCell: (params) => {
        const url = getMediaUrl(params.value);
        if (!url) return null;
        return (
          <Box
            component="img"
            src={url}
            alt="Proof"
            sx={{
              width: 40,
              height: 40,
              objectFit: 'cover',
              borderRadius: 1,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 },
            }}
            onClick={() => window.open(url, '_blank')}
          />
        );
      },
    },
    {
      field: 'user_email',
      headerName: 'User',
      minWidth: 180,
      flex: 1,
      valueGetter: (params) => params?.row?.user_email || '',
    },
    {
      field: 'order_number',
      headerName: 'Order',
      minWidth: 140,
      flex: 1,
      valueGetter: (params) => params?.row?.order_number || '',
    },
    {
      field: 'package_number',
      headerName: 'Package',
      minWidth: 140,
      flex: 1,
      valueGetter: (params) => params?.row?.package_number || '',
    },
    {
      field: 'created_at',
      headerName: 'Created',
      minWidth: 170,
      flex: 1,
      valueGetter: (params) => params?.row?.created_at || '',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 170,
      renderCell: (params) => {
        const row = params.row;
        if (!canMediate) {
          return (
            <Typography variant="caption" color="text.secondary">
              Read only
            </Typography>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {row.status !== 'approved' && (
              <Tooltip title="Approve">
                <IconButton size="small" color="success" onClick={() => handleApprove(row.id)}>
                  <CheckCircle fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {row.status !== 'rejected' && (
              <Tooltip title="Reject">
                <IconButton size="small" color="error" onClick={() => handleReject(row.id)}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={row.is_featured ? 'Unfeature' : 'Feature'}>
              <IconButton size="small" color={row.is_featured ? 'warning' : 'default'} onClick={() => handleToggleFeatured(row)}>
                <Star fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit display name/location">
              <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ]), [canMediate, handleApprove, handleOpenEdit, handleReject, handleToggleFeatured]);

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <PageMeta title="Customer reviews" />

      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 9 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: (theme) => theme.palette.primary.main }}>
              Customer reviews
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Reviews are visible publicly only after approval and only when publish permission is enabled.
            </Typography>
          </Box>
        </Grid>

        <Grid
          size={{ xs: 12, md: 3 }}
          sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
        >
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadReviews}
          >
            Refresh
          </Button>
        </Grid>
      </Grid>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Tabs value={statusTab} onChange={(_, v) => { setStatusTab(v); setPage(0); }} sx={{ minHeight: 42 }}>
            <Tab value="pending" label="Pending" />
            <Tab value="approved" label="Approved" />
            <Tab value="rejected" label="Rejected" />
          </Tabs>

          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ minWidth: 260, ml: 'auto' }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <div style={{ width: '100%', height: 640 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              loading={loading}
              hideFooter
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: (theme) => theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white',
                },
              }}
            />
          </div>
          <CustomPagination
            page={page}
            totalPages={totalPages}
            rowsPerPage={pageSize}
            onPageChange={setPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            customRowsActive={customRowsActive}
            customRowsInput={customRowsInput}
            onCustomRowsInputChange={handleCustomRowsInputChange}
            onCustomRowsInputBlur={handleCustomRowsInputBlur}
          />
        </CardContent>
      </Card>

      <Dialog open={editOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit review display</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Display name"
            value={editDisplayName}
            onChange={(e) => setEditDisplayName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Location"
            value={editLocation}
            onChange={(e) => setEditLocation(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={savingEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={savingEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
